import React, { useState } from 'react'
import { useField, useFormikContext } from 'formik'
import { InputFieldContainer } from './styles'
import { BaseFieldProps } from './index'
import { useNewTranslation } from 'hooks'
import { getFileSignature, UploadType } from 'services/api/uploads'
import { showSnackBar } from 'services/toast'
import { convertToBytes, FileSlice, parseFileSize, processFile } from 'services/cloudinary'
import FileUploader, { AcceptType, FileState } from 'particles/FileUploader'
import { create } from 'apisauce'
import { LabelSize } from 'particles/Label'

interface FileError {
    inlineError: boolean
    message?: string
}

interface ImageUploadInputProps extends BaseFieldProps {
    name: string
    presetType?: UploadType
    maxFiles: number
    uploadContainerWidth?: string
    requiredWidth?: number
    requiredHeight?: number
    placeholder?: string
    identifier?: string
    showError?: boolean // set false if want to use external error state to control the error display
    validateByTouch?: boolean // set to false if you want to use an external error state to control error display. It will show errors without requiring the field to be touched.
    dataTestId?: string
    maxWidth?: string
    labelSize?: LabelSize
    acceptTypes?: AcceptType[]
}

const ImageUploaderField: React.FC<ImageUploadInputProps> = ({
    presetType = UploadType.CAMPAIGN,
    maxFiles,
    title,
    subtitle,
    isRequired,
    uploadContainerWidth,
    requiredWidth,
    requiredHeight,
    placeholder,
    isDisable,
    isReadOnly = false,
    identifier,
    name,
    showError = true,
    validateByTouch = true,
    maxWidth,
    acceptTypes,
    ...props
}) => {
    const [t] = useNewTranslation()

    const [field, meta] = useField<any>(name)
    const { setFieldValue, setFieldTouched } = useFormikContext()
    const fieldValue = field.value || []
    const hasError = showError && !!meta.error && (!validateByTouch || meta.touched)

    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [errors, setErrors] = useState<(FileError | undefined)[]>([])
    const [selectedFiles, setSelectedFiles] = useState<FileState[]>([])

    const addItems = (data: any): void => {
        const items = fieldValue
        setFieldValue(field.name as never, items.concat(data))
    }

    const handleRemoveUploadedFile = (file: FileState, index: number): void => {
        const items = [...fieldValue]
        items.splice(index, 1)
        setFieldValue(field.name as never, items)
        if (items.length === 0) setFieldTouched(field.name as never, true, false)
    }

    const handleSubmit = (responses: any[]): void => {
        const uploadData = responses.map((response: any) => response.data)
        addItems(uploadData)
    }

    const imageLimit = Number(
        process.env.REACT_APP_CLOUDINARY_IMAGE_LIMIT ? process.env.REACT_APP_CLOUDINARY_IMAGE_LIMIT : 10,
    )
    const videoLimit = Number(
        process.env.REACT_APP_CLOUDINARY_VIDEO_LIMIT ? process.env.REACT_APP_CLOUDINARY_VIDEO_LIMIT : 100,
    )
    const FILE_SIZE_TOO_LARGE_ERROR: FileError = {
        inlineError: true,
        message: t('errorMessage.general.uploadSmallerPhotosVideos', {
            imageLimit: parseFileSize(imageLimit),
            videoLimit: parseFileSize(videoLimit),
        }),
    }

    const api = create({
        baseURL: `https://api.cloudinary.com/v1_1/${process.env.REACT_APP_CLOUDINARY_CLOUD_NAME}/auto`,
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
        },
    })

    const getBody = (file: Blob, fileSignature: any): FormData => {
        const body = new FormData()
        body.append('file', file)
        body.append('api_key', process.env.REACT_APP_CLOUDINARY_API_KEY || '')
        body.append('timestamp', '' + fileSignature.timestamp)
        body.append('signature', '' + fileSignature.signature)
        body.append('public_id', '' + fileSignature.public_id)
        body.append('upload_preset', '' + fileSignature.upload_preset)
        body.append('tags', '' + fileSignature.tags.join(','))
        return body
    }

    const handleUpload = (files: FileState[]): void => {
        setIsLoading(true)
        getFileSignature({
            preset_type: presetType,
            quantity: files.length,
        }).then((response: any) => {
            setIsLoading(false)
            if (response.data && response.data.success) {
                setIsLoading(true)
                const BREAKPOINT_IN_BYTES = convertToBytes(
                    Number(
                        process.env.REACT_APP_CLOUDINARY_CHUNKING_BREAKPOINT
                            ? process.env.REACT_APP_CLOUDINARY_CHUNKING_BREAKPOINT
                            : 100,
                    ),
                )
                const uploaders = files.map((file: File, index: number) => {
                    const fileSignature = response.data.data[index]
                    if (file.size > BREAKPOINT_IN_BYTES) {
                        const slices = processFile(file)
                        const uniqueId = +new Date()
                        const chunks = slices.map((slice: FileSlice) => {
                            const body = getBody(slice.data, fileSignature)
                            return api.post('/upload', body, {
                                headers: {
                                    'X-Unique-Upload-Id': uniqueId,
                                    'Content-Range': 'bytes ' + slice.start + '-' + slice.end + '/' + slice.size,
                                },
                            })
                        })
                        return Promise.all(chunks)
                    } else {
                        return Promise.all([api.post('/upload', getBody(file, fileSignature))])
                    }
                })

                Promise.all(uploaders).then((responses: any) => {
                    const successIndexs: number[] = []
                    const successResponses = responses
                        .filter((imageResponses: any[], index: number) => {
                            const success =
                                imageResponses.length > 1
                                    ? imageResponses.every(response => response.ok) &&
                                      imageResponses.find(response => response.data && response.data.done)
                                    : imageResponses.every(response => response.ok)
                            if (success) successIndexs.push(index)
                            return success
                        })
                        .map((imageResponses: any[]) => {
                            const imageResponse = imageResponses.find(
                                response => response.data && response.data.asset_id,
                            )
                            return {
                                ...imageResponse,
                                data: {
                                    ...imageResponse.data,
                                    ...{
                                        file_format: imageResponse ? imageResponse.data.format : '',
                                    },
                                },
                            }
                        })
                    const errorMessages = responses
                        .filter((imageResponses: any[], index: number) => !successIndexs.includes(index))
                        .map((imageResponses: any[]) => {
                            const failResponse = imageResponses.find(response => !response.ok)
                            if (failResponse.problem === 'NETWORK_ERROR') {
                                showSnackBar({
                                    title: t('errorMessage.general.cantConnectToServerSubtitle'),
                                    type: 'error',
                                })
                                return { inlineError: false }
                            } else if (failResponse.status === 500) {
                                showSnackBar({
                                    title: t('errorMessage.general.cantUploadFileTitle'),
                                    type: 'error',
                                })
                                return { inlineError: false }
                            } else if (failResponse.status === 400) {
                                if (
                                    failResponse.data &&
                                    failResponse.data.error &&
                                    failResponse.data.error.message &&
                                    failResponse.data.error.message.includes('File size too large.')
                                ) {
                                    showSnackBar({
                                        title: t('errorMessage.validation.photoVideoTooLargeTitle'),
                                        type: 'error',
                                    })
                                    return FILE_SIZE_TOO_LARGE_ERROR
                                }
                            }
                            showSnackBar({
                                title: t('errorMessage.general.unknownError'),
                                type: 'error',
                            })
                            return { inlineError: false }
                        })
                    setErrors(errorMessages)
                    setIsLoading(false)
                    setSelectedFiles([])
                    handleSubmit(successResponses)
                })
            } else {
                showSnackBar({
                    title: t('errorMessage.general.unknownError'),
                    type: 'error',
                })
            }
        })
    }

    const defaultTypes: AcceptType[] = ['image']

    if (presetType !== UploadType.BRAND) {
        defaultTypes.push('video')
    }

    return (
        <InputFieldContainer maxWidth={maxWidth}>
            <FileUploader
                label={title}
                subtext={subtitle}
                maxFiles={maxFiles}
                placeholder={placeholder}
                uploadContainerWidth={uploadContainerWidth}
                isDisabled={isDisable}
                acceptTypes={acceptTypes ?? defaultTypes}
                onUpload={handleUpload}
                onRemoveUploadedFile={handleRemoveUploadedFile}
                uploadedFiles={fieldValue}
                selectedFiles={selectedFiles}
                isLoading={isLoading}
                errorMessage={hasError ? meta.error : ''}
                selectedFilesErrors={errors}
                requiredHeight={requiredHeight}
                requiredWidth={requiredWidth}
                identifier={identifier}
                isReadOnly={isReadOnly}
                isRequired={isRequired}
                {...props}
            />
        </InputFieldContainer>
    )
}

export default ImageUploaderField
